/**
 * Servicio de caché para almacenar respuestas de API
 * Usa localStorage con expiración automática
 */

const CACHE_PREFIX = 'reborn_';
const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutos en milisegundos

/**
 * Obtiene una clave de caché basada en la URL
 */
const getCacheKey = (url) => {
    return `${CACHE_PREFIX}${btoa(url).replace(/[+/=]/g, '')}`;
};

/**
 * Guarda datos en el caché con timestamp
 */
export const setCache = (url, data, cacheTime = DEFAULT_CACHE_TIME) => {
    try {
        const cacheKey = getCacheKey(url);
        const cacheData = {
            data,
            timestamp: Date.now(),
            expiresAt: Date.now() + cacheTime
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
        // Si localStorage está lleno o no disponible, simplemente no guardar
        if (process.env.NODE_ENV === 'development') {
            console.warn('Error saving to cache:', error);
        }
    }
};

/**
 * Obtiene datos del caché si aún no han expirado
 */
export const getCache = (url) => {
    try {
        const cacheKey = getCacheKey(url);
        const cached = localStorage.getItem(cacheKey);
        
        if (!cached) {
            return null;
        }

        const cacheData = JSON.parse(cached);
        const now = Date.now();

        // Verificar si el caché ha expirado
        if (now > cacheData.expiresAt) {
            // Eliminar caché expirado
            localStorage.removeItem(cacheKey);
            return null;
        }

        return cacheData.data;
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('Error reading from cache:', error);
        }
        return null;
    }
};

/**
 * Limpia el caché expirado
 */
export const cleanExpiredCache = () => {
    try {
        const keys = Object.keys(localStorage);
        const now = Date.now();

        keys.forEach(key => {
            if (key.startsWith(CACHE_PREFIX)) {
                try {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        const cacheData = JSON.parse(cached);
                        if (now > cacheData.expiresAt) {
                            localStorage.removeItem(key);
                        }
                    }
                } catch (error) {
                    // Si hay error al leer, eliminar la clave
                    localStorage.removeItem(key);
                }
            }
        });
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('Error cleaning cache:', error);
        }
    }
};

/**
 * Limpia todo el caché
 */
export const clearCache = () => {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith(CACHE_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('Error clearing cache:', error);
        }
    }
};

/**
 * Función fetch con caché automático
 * @param {string} url - URL a obtener
 * @param {object} options - Opciones de fetch
 * @param {number} cacheTime - Tiempo de caché en milisegundos (default: 5 minutos)
 * @returns {Promise} - Promise con los datos
 */
export const fetchWithCache = async (url, options = {}, cacheTime = DEFAULT_CACHE_TIME) => {
    // Limpiar caché expirado antes de buscar
    cleanExpiredCache();

    // Intentar obtener del caché primero
    const cachedData = getCache(url);
    if (cachedData !== null) {
        if (process.env.NODE_ENV === 'development') {
            console.log('Cache hit for:', url);
        }
        return cachedData;
    }

    // Si no hay caché, hacer la petición
    try {
        const response = await fetch(url, options);
        const data = await response.json();
        
        // Guardar en caché solo si la respuesta fue exitosa
        if (response.ok) {
            setCache(url, data, cacheTime);
        }
        
        return data;
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.error('Error fetching:', url, error);
        }
        throw error;
    }
};

// Limpiar caché expirado al cargar el módulo
if (typeof window !== 'undefined') {
    cleanExpiredCache();
}

